import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import kwikMemoriesLogoIcon from "../../../assets/Icons/login/kwikmemories_logo.webp";
import kwikMemoriesWhiteLogoIcon from "../../../assets/images/marketplace/KM_whitelogo.webp";
import { getMarketplaceProfile } from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { useMarketplaceTheme } from "./MarketplaceTheme";
import { isProfileCompleted } from "./MarketplaceShared";
import {
  FooterWrap,
  FooterInner,
  FooterTop,
  FooterBrandCol,
  LogoArea,
  LogoImg,
  FooterTagline,
  FooterCol,
  FooterColTitle,
  FooterLinks,
  FooterLink,
  FooterBottom,
  FooterCopyright,
} from "./MarketplaceFooterStyles";

const MarketplaceFooter: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useMarketplaceTheme();
  const [hasProfile, setHasProfile] = useState<boolean>(() => {
    const cached = localStorage.getItem("marketplace_profile");
    if (cached) {
      try {
        const parsed = JSON.parse(cached);
        const prof = parsed?.data || parsed;
        return isProfileCompleted(prof);
      } catch {
        return false;
      }
    }
    return false;
  });

  useEffect(() => {
    let isMounted = true;

    const checkProfileStatus = () => {
      const token = localStorage.getItem("access_token");
      if (token) {
        const cached = localStorage.getItem("marketplace_profile");
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            const prof = parsed?.data || parsed;
            setHasProfile(isProfileCompleted(prof));
          } catch {
            setHasProfile(false);
          }
        }

        getMarketplaceProfile()
          .then((res) => {
            if (!isMounted) return;
            const profileData = res?.data || null;
            const exists = isProfileCompleted(profileData);
            setHasProfile(exists);
            if (exists) {
              localStorage.setItem("marketplace_profile", JSON.stringify(profileData));
            } else {
              localStorage.removeItem("marketplace_profile");
            }
          })
          .catch(() => {
            if (!isMounted) return;
            const stillCached = localStorage.getItem("marketplace_profile");
            if (stillCached) {
              try {
                const parsed = JSON.parse(stillCached);
                const prof = parsed?.data || parsed;
                const valid = isProfileCompleted(prof);
                setHasProfile(valid);
                if (!valid) localStorage.removeItem("marketplace_profile");
              } catch {
                setHasProfile(false);
                localStorage.removeItem("marketplace_profile");
              }
            } else {
              setHasProfile(false);
            }
          });
      } else {
        setHasProfile(false);
      }
    };

    checkProfileStatus();
    window.addEventListener("marketplace-auth-change", checkProfileStatus);
    window.addEventListener("storage", checkProfileStatus);
    return () => {
      isMounted = false;
      window.removeEventListener("marketplace-auth-change", checkProfileStatus);
      window.removeEventListener("storage", checkProfileStatus);
    };
  }, []);

  const handleNav = (path: string) => {
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(path);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleJoinAsPro = () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    if (!hasProfile) {
      handleNav("/marketplace/profile/create");
    }
  };

  return (
    <FooterWrap>
      <FooterInner>
        <FooterTop>
          {/* Brand */}
          <FooterBrandCol>
            <LogoArea onClick={() => handleNav("/marketplace")}>
              <LogoImg src={theme === "dark" ? kwikMemoriesWhiteLogoIcon : kwikMemoriesLogoIcon} alt="KwikMemories Logo" />
            </LogoArea>
            <FooterTagline>
              The premier marketplace for professional creatives to buy, sell, and rent top-tier production equipment and talent.
            </FooterTagline>
          </FooterBrandCol>

          {/* Marketplace */}
          <FooterCol>
            <FooterColTitle>Marketplace</FooterColTitle>
            <FooterLinks>
              <FooterLink><span onClick={() => handleNav("/marketplace/equipment")}>Buy Gear</span></FooterLink>
              <FooterLink><span onClick={() => handleNav("/marketplace/sell")}>Sell Gear</span></FooterLink>
              <FooterLink><span onClick={() => handleNav("/marketplace/equipment")}>Rent Equipment</span></FooterLink>
            </FooterLinks>
          </FooterCol>

          {/* Professionals */}
          <FooterCol>
            <FooterColTitle>Professionals</FooterColTitle>
            <FooterLinks>
              <FooterLink><span onClick={() => handleNav("/marketplace/talent")}>Hire Talent</span></FooterLink>
              {!hasProfile && (
                <FooterLink><span onClick={handleJoinAsPro}>Join as Pro</span></FooterLink>
              )}
              {/* <FooterLink><span onClick={() => handleNav("/marketplace")}>Referral Program</span></FooterLink> */}
            </FooterLinks>
          </FooterCol>

          {/* Company */}
          <FooterCol>
            <FooterColTitle>Company</FooterColTitle>
            <FooterLinks>
              {/* <FooterLink><span onClick={() => handleNav("/marketplace/about")}>About Us</span></FooterLink> */}
              {/* <FooterLink><span onClick={() => handleNav("/marketplace")}>Careers</span></FooterLink> */}
              <FooterLink><span onClick={() => window.open("https://wa.me/919875082248", "_blank", "noopener,noreferrer")}>Contact</span></FooterLink>
            </FooterLinks>
          </FooterCol>
        </FooterTop>

        <FooterBottom>
          <FooterCopyright>© 2026 KwikMemories All rights reserved.</FooterCopyright>
        </FooterBottom>
      </FooterInner>
    </FooterWrap>
  );
};

export default MarketplaceFooter;
